import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlayFab, PlayFabClient, PlayFabCloudScript } from 'playfab-sdk';
import {
  getAllTimeStatsFromProfile,
  getLevelProgressFromXp,
  PlayerStats,
  ProfileConstraints,
  TitleId,
} from '../lib/playfab';
import { ArenaSpec } from '../lib/arenaExport';

type PlayFabWithSettings = typeof PlayFab & {
  _internalSettings: {
    sessionTicket: string | null;
    entityToken: string | null | undefined;
  };
  settings: {
    titleId: string;
  };
};
(PlayFab as PlayFabWithSettings).settings.titleId = TitleId;

type GenericResult = {
  success: boolean;
  error?: string;
};

export type UploadArenaLayoutResult = {
  success: boolean;
  error?: string;
  itemId?: string;
  friendlyId?: string;
};

// UpdateArenaLayout's Azure Function returns the same {success, error,
// itemId, friendlyId} shape as UploadArenaLayout.
export type UpdateArenaLayoutResult = UploadArenaLayoutResult;

export type ArenaLayoutSummary = {
  itemId: string;
  friendlyId?: string;
  name?: string;
  contentUrl?: string;
  createdDate?: string;
  lastModifiedDate?: string;
};

export type ListMyArenaLayoutsResult = {
  success: boolean;
  error?: string;
  layouts?: ArenaLayoutSummary[];
};

export type GetArenaLayoutForEditResult = {
  success: boolean;
  error?: string;
  itemId?: string;
  friendlyId?: string;
  name?: string;
  arenaSpec?: ArenaSpec;
  contentUrl?: string;
};

export type DeleteArenaLayoutResult = {
  success: boolean;
  error?: string;
};

export type PlayfabClientApi = {
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  setAvatarUrl: (url: string) => Promise<boolean>;
  setDisplayName: (displayName: string) => Promise<boolean>;
  uploadArenaLayout: (
    name: string,
    arenaSpec: ArenaSpec
  ) => Promise<UploadArenaLayoutResult>;
  listMyArenaLayouts: () => Promise<ListMyArenaLayoutsResult>;
  getArenaLayoutForEdit: (itemId: string) => Promise<GetArenaLayoutForEditResult>;
  updateArenaLayout: (
    itemId: string,
    arenaSpec: ArenaSpec
  ) => Promise<UpdateArenaLayoutResult>;
  deleteArenaLayout: (itemId: string) => Promise<DeleteArenaLayoutResult>;
};

export type PlayfabAuthContextType = {
  api: PlayfabClientApi;
  isLoggedIn?: boolean;
  entityKey?: PlayFabClientModels.EntityKey;
  profile?: PlayFabClientModels.PlayerProfileModel;
  stats?: PlayerStats;
  levelProgress?: number;
  avatarLastUpdatedAt?: Date;
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const PlayfabAuthContext = React.createContext<PlayfabAuthContextType>(null!);

export const PlayfabAuthContextProvider: React.FC = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [entityKey, setEntityKey] = useState<PlayFabClientModels.EntityKey>();
  const [profile, setProfile] =
    useState<PlayFabClientModels.PlayerProfileModel>();
  const [stats, setStats] = useState<PlayerStats>();
  const [levelProgress, setLevelProgress] = useState(0);
  const [avatarLastUpdatedAt, setAvatarLastUpdatedAt] = useState<Date>();

  const clearSession = useCallback(() => {
    const internalSettings = (PlayFab as PlayFabWithSettings)._internalSettings;
    internalSettings.sessionTicket = null;
    internalSettings.entityToken = null;
    window.sessionStorage.removeItem('entityToken');
    window.sessionStorage.removeItem('sessionTicket');
    window.sessionStorage.removeItem('entityKey');
  }, []);

  const updateProfileData = useCallback(
    (profile?: PlayFabClientModels.PlayerProfileModel) => {
      if (!profile) {
        setStats(undefined);
        setLevelProgress(0);
        return;
      }

      const newStats = getAllTimeStatsFromProfile(profile);
      const newLevelProgress = newStats?.xp
        ? getLevelProgressFromXp(newStats.xp)
        : 0;
      setStats(newStats);
      setLevelProgress(newLevelProgress);
      setProfile(profile);
    },
    []
  );

  const login = useCallback<PlayfabClientApi['login']>(
    async (email, password) => {
      try {
        const result = await new Promise<PlayFabClientModels.LoginResult>(
          (resolve, reject) => {
            PlayFabClient.LoginWithEmailAddress(
              {
                Email: email,
                Password: password,
                InfoRequestParameters: {
                  GetPlayerProfile: true,
                  GetCharacterInventories: false,
                  GetCharacterList: false,
                  GetPlayerStatistics: false,
                  GetTitleData: false,
                  GetUserAccountInfo: false,
                  GetUserData: false,
                  GetUserInventory: false,
                  GetUserReadOnlyData: false,
                  GetUserVirtualCurrency: false,
                  ProfileConstraints,
                },
              },
              (error, result) => (error ? reject(error) : resolve(result.data))
            );
          }
        );

        const sessionTicket = result.SessionTicket;
        const entityToken = result.EntityToken?.EntityToken;
        const entity = result.EntityToken?.Entity;

        if (!sessionTicket || !entityToken || !entity) {
          throw new Error('Invalid login response');
        }

        // Update PlayFab internal state
        const internalSettings = (PlayFab as PlayFabWithSettings)
          ._internalSettings;
        internalSettings.sessionTicket = sessionTicket;
        internalSettings.entityToken = entityToken;

        // Persist session
        window.sessionStorage.setItem('sessionTicket', sessionTicket);
        window.sessionStorage.setItem('entityToken', entityToken);
        window.sessionStorage.setItem('entityKey', JSON.stringify(entity));

        // Update application state
        setIsLoggedIn(true);
        setEntityKey(entity);
        updateProfileData(result.InfoResultPayload?.PlayerProfile);
        return true;
      } catch (error) {
        const e = error as PlayFabModule.IPlayFabError;
        const message = e?.errorDetails
          ? Object.values(e.errorDetails as Record<string, string[]>)
              .map((x) => (x ? x[0] : ''))
              .reduce((a, b) => `${a}\n${b}`)
          : e?.errorMessage || error;
        alert(`Could not log in:\n${message}`);
        return false;
      }
    },
    [updateProfileData]
  );

  const logout = useCallback(() => {
    clearSession();
    setIsLoggedIn(false);
    setEntityKey(undefined);
    updateProfileData(undefined);
  }, [clearSession, updateProfileData]);

  const setAvatarUrl = useCallback<PlayfabClientApi['setAvatarUrl']>(
    async (url) => {
      try {
        await new Promise((resolve, reject) => {
          PlayFabClient.UpdateAvatarUrl({ ImageUrl: url }, (error, result) =>
            error ? reject(error) : resolve(result)
          );
        });

        setProfile((prev) => (prev ? { ...prev, AvatarUrl: url } : undefined));
        setAvatarLastUpdatedAt(new Date());
        return true;
      } catch (error) {
        console.error('Failed to update avatar URL:', error);
        return false;
      }
    },
    []
  );

  const setDisplayName = useCallback<PlayfabClientApi['setDisplayName']>(
    async (DisplayName) => {
      try {
        await new Promise((resolve, reject) => {
          PlayFabClient.UpdateUserTitleDisplayName(
            { DisplayName },
            (error, result) =>
              error ? reject(error.errorMessage) : resolve(result)
          );
        });
        setProfile((prev) => (prev ? { ...prev, DisplayName } : undefined));
        return true;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    []
  );

  const uploadArenaLayout = useCallback<PlayfabClientApi['uploadArenaLayout']>(
    async (name, arenaSpec) => {
      try {
        // This title's CloudScript is implemented as registered Azure Functions
        // (HTTP-triggered), not classic CloudScript revisions — so the client
        // must call ExecuteFunction (entity-token authenticated), not the
        // classic ExecuteCloudScript. PlayFab wraps FunctionParameter into the
        // `FunctionArgument` field of the request body it sends to the Azure
        // Function. See snapshot-az/UploadArenaLayout.cs.
        const result = await new Promise<PlayFabCloudScriptModels.ExecuteFunctionResult>(
          (resolve, reject) => {
            PlayFabCloudScript.ExecuteFunction(
              {
                FunctionName: 'UploadArenaLayout',
                FunctionParameter: {
                  name,
                  arenaSpec,
                  // The function derives the verified uploader id from
                  // PlayFab's own CallerEntityProfile (server-populated from
                  // the entity token), not from this field — playfabId here
                  // is only a fallback for direct/manual testing outside
                  // PlayFab's ExecuteFunction routing. displayName has no
                  // server-verified source, so it's sent as a cosmetic label
                  // for DisplayProperties.uploaderDisplayName only.
                  playfabId: profile?.PlayerId,
                  displayName: profile?.DisplayName,
                },
              },
              (error, result) => (error ? reject(error) : resolve(result.data))
            );
          }
        );

        if (result.Error) {
          return { success: false, error: result.Error.Message || result.Error.Error };
        }

        const functionResult = result.FunctionResult as
          | { error?: string; itemId?: string; friendlyId?: string }
          | undefined;

        if (functionResult?.error) {
          return { success: false, error: functionResult.error };
        }

        return {
          success: true,
          itemId: functionResult?.itemId,
          friendlyId: functionResult?.friendlyId,
        };
      } catch (error) {
        const e = error as PlayFabModule.IPlayFabError;
        return { success: false, error: e?.errorMessage || 'Upload failed.' };
      }
    },
    [profile]
  );

  // profile.PlayerId is the classic/master PlayFabId (confirmed via the
  // avatar S3 key, which is keyed by this same id) — used as the
  // masterPlayerAccountId fallback below. Like uploadArenaLayout's
  // playfabId, these are only fallbacks for direct/manual testing outside
  // PlayFab's ExecuteFunction routing; real calls derive verified identity
  // server-side from CallerEntityProfile. See snapshot-az/ListMyArenaLayouts.cs.
  const listMyArenaLayouts = useCallback<PlayfabClientApi['listMyArenaLayouts']>(
    async () => {
      try {
        const result = await new Promise<PlayFabCloudScriptModels.ExecuteFunctionResult>(
          (resolve, reject) => {
            PlayFabCloudScript.ExecuteFunction(
              {
                FunctionName: 'ListMyArenaLayouts',
                FunctionParameter: {
                  playfabId: profile?.PlayerId,
                  masterPlayerAccountId: profile?.PlayerId,
                },
              },
              (error, result) => (error ? reject(error) : resolve(result.data))
            );
          }
        );

        if (result.Error) {
          return { success: false, error: result.Error.Message || result.Error.Error };
        }

        const functionResult = result.FunctionResult as
          | { error?: string; layouts?: ArenaLayoutSummary[] }
          | undefined;

        if (functionResult?.error) {
          return { success: false, error: functionResult.error };
        }

        return { success: true, layouts: functionResult?.layouts ?? [] };
      } catch (error) {
        const e = error as PlayFabModule.IPlayFabError;
        return { success: false, error: e?.errorMessage || 'Could not load your layouts.' };
      }
    },
    [profile]
  );

  const getArenaLayoutForEdit = useCallback<PlayfabClientApi['getArenaLayoutForEdit']>(
    async (itemId) => {
      try {
        const result = await new Promise<PlayFabCloudScriptModels.ExecuteFunctionResult>(
          (resolve, reject) => {
            PlayFabCloudScript.ExecuteFunction(
              {
                FunctionName: 'ListMyArenaLayouts',
                FunctionParameter: {
                  itemId,
                  playfabId: profile?.PlayerId,
                  masterPlayerAccountId: profile?.PlayerId,
                },
              },
              (error, result) => (error ? reject(error) : resolve(result.data))
            );
          }
        );

        if (result.Error) {
          return { success: false, error: result.Error.Message || result.Error.Error };
        }

        const functionResult = result.FunctionResult as
          | {
              error?: string;
              itemId?: string;
              friendlyId?: string;
              name?: string;
              arenaSpec?: ArenaSpec;
              contentUrl?: string;
            }
          | undefined;

        if (functionResult?.error) {
          return { success: false, error: functionResult.error };
        }

        return {
          success: true,
          itemId: functionResult?.itemId,
          friendlyId: functionResult?.friendlyId,
          name: functionResult?.name,
          arenaSpec: functionResult?.arenaSpec,
          contentUrl: functionResult?.contentUrl,
        };
      } catch (error) {
        const e = error as PlayFabModule.IPlayFabError;
        return { success: false, error: e?.errorMessage || 'Could not load that layout.' };
      }
    },
    [profile]
  );

  const updateArenaLayout = useCallback<PlayfabClientApi['updateArenaLayout']>(
    async (itemId, arenaSpec) => {
      try {
        const result = await new Promise<PlayFabCloudScriptModels.ExecuteFunctionResult>(
          (resolve, reject) => {
            PlayFabCloudScript.ExecuteFunction(
              {
                FunctionName: 'UpdateArenaLayout',
                FunctionParameter: {
                  itemId,
                  arenaSpec,
                  playfabId: profile?.PlayerId,
                },
              },
              (error, result) => (error ? reject(error) : resolve(result.data))
            );
          }
        );

        if (result.Error) {
          return { success: false, error: result.Error.Message || result.Error.Error };
        }

        const functionResult = result.FunctionResult as
          | { error?: string; itemId?: string; friendlyId?: string }
          | undefined;

        if (functionResult?.error) {
          return { success: false, error: functionResult.error };
        }

        return {
          success: true,
          itemId: functionResult?.itemId,
          friendlyId: functionResult?.friendlyId,
        };
      } catch (error) {
        const e = error as PlayFabModule.IPlayFabError;
        return { success: false, error: e?.errorMessage || 'Update failed.' };
      }
    },
    [profile]
  );

  const deleteArenaLayout = useCallback<PlayfabClientApi['deleteArenaLayout']>(
    async (itemId) => {
      try {
        const result = await new Promise<PlayFabCloudScriptModels.ExecuteFunctionResult>(
          (resolve, reject) => {
            PlayFabCloudScript.ExecuteFunction(
              {
                FunctionName: 'DeleteArenaLayout',
                FunctionParameter: {
                  itemId,
                  playfabId: profile?.PlayerId,
                  masterPlayerAccountId: profile?.PlayerId,
                },
              },
              (error, result) => (error ? reject(error) : resolve(result.data))
            );
          }
        );

        if (result.Error) {
          return { success: false, error: result.Error.Message || result.Error.Error };
        }

        const functionResult = result.FunctionResult as { error?: string } | undefined;

        if (functionResult?.error) {
          return { success: false, error: functionResult.error };
        }

        return { success: true };
      } catch (error) {
        const e = error as PlayFabModule.IPlayFabError;
        return { success: false, error: e?.errorMessage || 'Delete failed.' };
      }
    },
    [profile]
  );

  const reactivateSession = useCallback(async () => {
    const sessionTicket = window.sessionStorage.getItem('sessionTicket');
    const entityToken = window.sessionStorage.getItem('entityToken');
    const storedEntityKey = window.sessionStorage.getItem('entityKey');

    if (!sessionTicket || !entityToken || !storedEntityKey) {
      clearSession();
      return;
    }

    try {
      const internalSettings = (PlayFab as PlayFabWithSettings)
        ._internalSettings;
      internalSettings.sessionTicket = sessionTicket;
      internalSettings.entityToken = entityToken;

      const entity = JSON.parse(storedEntityKey);
      const profileResult =
        await new Promise<PlayFabClientModels.GetPlayerProfileResult>(
          (resolve, reject) => {
            PlayFabClient.GetPlayerProfile(
              { ProfileConstraints },
              (error, result) => (error ? reject(error) : resolve(result.data))
            );
          }
        );

      console.log('Reactivated Session');
      setIsLoggedIn(true);
      setEntityKey(entity);
      updateProfileData(profileResult.PlayerProfile);
      setAvatarLastUpdatedAt(new Date());
    } catch (error) {
      console.error('Session reactivation failed:', error);
      clearSession();
    }
  }, [clearSession, updateProfileData]);

  useEffect(() => {
    const sessionExists = window.sessionStorage.getItem('sessionTicket');
    if (sessionExists && !isLoggedIn) {
      reactivateSession();
    }
  }, [isLoggedIn, reactivateSession]);

  const api = useMemo<PlayfabClientApi>(
    () => ({
      login,
      logout,
      setAvatarUrl,
      setDisplayName,
      uploadArenaLayout,
      listMyArenaLayouts,
      getArenaLayoutForEdit,
      updateArenaLayout,
      deleteArenaLayout,
    }),
    [
      login,
      logout,
      setAvatarUrl,
      setDisplayName,
      uploadArenaLayout,
      listMyArenaLayouts,
      getArenaLayoutForEdit,
      updateArenaLayout,
      deleteArenaLayout,
    ]
  );

  const contextValue = useMemo(
    () => ({
      api,
      isLoggedIn,
      entityKey,
      profile,
      stats,
      levelProgress,
      avatarLastUpdatedAt,
    }),
    [
      api,
      isLoggedIn,
      entityKey,
      profile,
      stats,
      levelProgress,
      avatarLastUpdatedAt,
    ]
  );

  return (
    <PlayfabAuthContext.Provider value={contextValue}>
      {children}
    </PlayfabAuthContext.Provider>
  );
};

export const usePlayfabAuthContext = () => React.useContext(PlayfabAuthContext);
